import { SubjectCriteria } from '@src/main/modules/messages/domain/subject/subject-criteria.interface';
import { Subject } from '@src/main/modules/messages/domain/subject/subject.interface';
import { SubjectRepository } from '@src/main/modules/messages/domain/subject/subject.repository';
import { CreateSubjectRequest } from '@src/main/modules/messages/infrastructure/dto/createSubjectRequest';
import { UpdateSubjectRequest } from '@src/main/modules/messages/infrastructure/dto/updateSubjectRequest';
import {
  SubjectEntity,
  toSubject,
} from '@src/main/modules/messages/infrastructure/entity/subject.entity';
import { api, TokenProvider } from '@src/main/shared/infrastructure/entity/axios';
import { PageResponse } from '@src/main/shared/infrastructure/entity/page.entity';

const path = '/subjects';

export const createSubjectHttpRepository = (tokenProvider: TokenProvider): SubjectRepository =>
  new SubjectHttpRepository(tokenProvider);

class SubjectHttpRepository implements SubjectRepository {
  private readonly tokenProvider: TokenProvider;

  constructor(tokenProvider: TokenProvider) {
    this.tokenProvider = tokenProvider;
  }

  private async getAuthToken(): Promise<string> {
    const token = await this.tokenProvider.getToken();
    if (!token) throw new Error('Authentication token is missing');
    return token;
  }

  async findByCriteria(criteria: SubjectCriteria): Promise<PageResponse<Subject>> {
    const token = await this.getAuthToken();
    const params = criteria;

    const response = await api.get<PageResponse<SubjectEntity>>(path, token, params);

    return {
      ...response,
      items: response.items.map(toSubject),
    };
  }

  async findAll(): Promise<Subject[]> {
    const token = await this.getAuthToken();
    const response = await api.get<SubjectEntity[]>(`${path}/all`, token);
    return response.map(toSubject);
  }

  async findById(id: string): Promise<Subject> {
    const token = await this.getAuthToken();
    const response = await api.get<SubjectEntity>(`${path}/${id}`, token);
    return toSubject(response);
  }

  async create(subject: CreateSubjectRequest): Promise<Subject> {
    const token = await this.getAuthToken();
    const response = await api.post<SubjectEntity>(path, token, subject);
    return toSubject(response);
  }

  async update(subject: UpdateSubjectRequest): Promise<Subject> {
    const token = await this.getAuthToken();
    const response = await api.put<SubjectEntity>(`${path}/${subject.subjectId}`, token, subject);
    return toSubject(response);
  }

  async delete(id: string): Promise<void> {
    const token = await this.getAuthToken();
    await api.delete(`${path}/${id}`, token);
  }
}
